import React from 'react'
import {
  IconThumbsUp,
  IconThumbsDown,
  IconClipboard,
  Button,
  GreyGrey90,
  Primary13Regular,
  Box,
  Flex
} from '@sequencehq/core-components'
import ReactMarkdown from 'react-markdown'
import {
  GenerativeComponent,
  GenerativeComponentData
} from '../generativeComponents'

interface BaseMessageBubbleProps {
  author: 'user' | 'assistant'
  showActions?: boolean
}

interface MessageBubbleTextProps extends BaseMessageBubbleProps {
  type: 'text'
  children: string | null | undefined
}

interface MessageBubbleCustomProps extends BaseMessageBubbleProps {
  type: 'custom'
  children: React.ReactNode | string | null | undefined
}

interface MessageBubbleComponentProps extends BaseMessageBubbleProps {
  type: 'component'
  component?: GenerativeComponentData
  components?: GenerativeComponentData[]
  textContent?: string
}

type MessageBubbleProps =
  | MessageBubbleTextProps
  | MessageBubbleCustomProps
  | MessageBubbleComponentProps

const ActionButtons = () => {
  return (
    <Flex gap={1}>
      <Button
        aria-label="Copy"
        size="24px"
        variant="secondary"
        icon={<IconClipboard color="#485972" />}
      />
      <Button
        aria-label="Thumb up"
        size="24px"
        variant="secondary"
        icon={<IconThumbsUp color="#485972" />}
      />
      <Button
        aria-label="Thumb down"
        size="24px"
        variant="secondary"
        icon={<IconThumbsDown color="#485972" />}
      />
    </Flex>
  )
}

// Custom styles for ReactMarkdown to fix indentation and formatting
// definitely still needs work
const markdownStyles = {
  '& ul': {
    paddingLeft: '20px',
    marginBottom: '12px',
    marginTop: '8px',
    listStyleType: 'disc',
    listStylePosition: 'outside'
  },
  '& ol': {
    paddingLeft: '20px',
    marginBottom: '12px',
    marginTop: '8px',
    listStyleType: 'decimal',
    listStylePosition: 'outside'
  },
  '& li': {
    marginBottom: '4px',
    paddingLeft: '4px',
    lineHeight: '20px'
  },
  '& li ul, & li ol': {
    marginTop: '4px',
    marginBottom: '4px'
  },
  '& p': {
    marginBottom: '12px',
    lineHeight: '20px',
    '&:last-child': {
      marginBottom: '0'
    }
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginBottom: '8px',
    marginTop: '16px',
    fontWeight: 'bold',
    '&:first-child': {
      marginTop: '0'
    }
  },
  '& h1': {
    fontSize: '18px'
  },
  '& h2': {
    fontSize: '16px'
  },
  '& h3': {
    fontSize: '14px'
  },
  '& h4': {
    fontSize: '13px'
  },
  '& strong': {
    fontWeight: 'bold'
  },
  '& em': {
    fontStyle: 'italic'
  },
  '& code': {
    backgroundColor: '#f5f5f5',
    padding: '2px 4px',
    borderRadius: '3px',
    fontSize: '12px',
    fontFamily: 'monospace'
  },
  '& pre': {
    backgroundColor: '#f5f5f5',
    padding: '12px',
    borderRadius: '6px',
    overflow: 'auto',
    marginBottom: '12px'
  },
  '& blockquote': {
    borderLeft: '4px solid #ddd',
    paddingLeft: '12px',
    marginLeft: '0',
    marginBottom: '12px',
    fontStyle: 'italic'
  }
}

// Had issues where Gemini was constantly adding <br> tags to the end of the message
const sanitize = (input?: string | null) => {
  if (!input) return ''
  return input
    .replace(/<br\s*\/?>(\s*)/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\[COMPONENT_PLACEHOLDER\]/g, '')
}

export const MessageBubble = (props: MessageBubbleProps) => {
  const { type, author } = props

  const content = (() => {
    switch (type) {
      case 'text':
        return (
          <Box sx={markdownStyles}>
            <ReactMarkdown>{sanitize(props.children as string)}</ReactMarkdown>
          </Box>
        )
      case 'component':
        return (
          <Box>
            {/* Render single component if provided and no components array is present */}
            {!props.components?.length && props.component && (
              <GenerativeComponent data={props.component} />
            )}

            {/* Render multiple components inline with text */}
            {props.components && props.components.length > 0 && (
              <Box sx={markdownStyles}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => {
                      // Replace placeholders inline when present
                      if (
                        typeof children === 'string' &&
                        children.includes('[COMPONENT_PLACEHOLDER]')
                      ) {
                        const parts = children.split('[COMPONENT_PLACEHOLDER]')
                        let componentIndex = 0
                        return (
                          <Box>
                            {parts.map((text, index) => (
                              <React.Fragment key={index}>
                                {text && (
                                  <Box as="p" mb="12px" lineHeight="20px">
                                    {text}
                                  </Box>
                                )}
                                {index < parts.length - 1 &&
                                  props.components &&
                                  props.components[componentIndex] && (
                                    <Box mb={4} width="100%">
                                      <GenerativeComponent
                                        data={
                                          props.components[componentIndex++]
                                        }
                                      />
                                    </Box>
                                  )}
                              </React.Fragment>
                            ))}
                          </Box>
                        )
                      }
                      return (
                        <Box as="p" mb="12px" lineHeight="20px">
                          {children}
                        </Box>
                      )
                    }
                  }}
                >
                  {sanitize(props.textContent || '')}
                </ReactMarkdown>
                {/* If no placeholders in the text, append components below in order */}
                {!String(props.textContent || '').includes(
                  '[COMPONENT_PLACEHOLDER]'
                ) && (
                  <Box mt={2}>
                    {props.components.map((c, i) => (
                      <Box key={i} mb={4} width="100%">
                        <GenerativeComponent data={c} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Fallback for textContent without components */}
            {props.textContent &&
              (!props.components || props.components.length === 0) && (
                <Box sx={markdownStyles} mt={2}>
                  <ReactMarkdown>{sanitize(props.textContent)}</ReactMarkdown>
                </Box>
              )}
          </Box>
        )
      case 'custom':
        return props.children
      default:
        return null
    }
  })()

  if (author === 'user') {
    return (
      <Box width="100%" position="relative">
        <Flex
          direction="column"
          gap={2.5}
          align="flex-end"
          justify="flex-start"
          width="100%"
        >
          <Box
            backgroundColor="#3742fa"
            maxWidth="420px"
            borderRadius="8px"
            position="relative"
          >
            <Flex
              align="center"
              justify="flex-end"
              maxWidth="inherit"
              overflow="hidden"
              position="relative"
              width="100%"
              height="100%"
            >
              <Flex
                display="flex"
                gap={1}
                align="center"
                justify="flex-end"
                maxWidth="inherit"
                p={2}
                position="relative"
              >
                <Box
                  color="white"
                  textAlign="left"
                  flex={1}
                  {...Primary13Regular}
                  lineHeight="18px"
                >
                  {content}
                </Box>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </Box>
    )
  }

  return (
    <Box width="100%" position="relative" py="16px">
      <Flex
        direction="column"
        align="flex-end"
        overflow="hidden"
        position="relative"
        width="100%"
        height="100%"
      >
        <Flex
          direction="column"
          gap={2}
          align="flex-start"
          justify="flex-start"
          px={2}
          py={4}
          position="relative"
          width="100%"
          maxWidth="calc(100vw - 100px)"
          {...Primary13Regular}
          color={GreyGrey90}
          paddingLeft="12px"
        >
          {content}
        </Flex>
        <ActionButtons />
      </Flex>
    </Box>
  )
}
